// Import ms-Grid and other grid related styles for schema lib to work correctly.
import '../Orchestrator/ExecutorPoolDetail.css';

import { RegisteredModel } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_pb';
import { GetRegisteredModelResponse } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_service_pb';
import { RegisteredModelVersion } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_pb';
import {
  GetModelVersionForExperimentRequest,
  GetModelVersionForExperimentResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_service_pb';
import { RegisteredModelService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_service_connect';
import { RegisteredModelVersionService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_version_service_connect';
import type { PlainMessage } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { DirectionalHint, IStyle, ITextFieldProps, MessageBarType, SpinnerSize, Toggle } from '@fluentui/react';
import {
  Button,
  buttonStylesPrimary,
  buttonStylesStealth,
  ClassNamesFromIStyles,
  CodeBlock,
  Dropdown,
  IH2OTheme,
  Info,
  Link,
  ListRow,
  Loader,
  loaderStylesSpinnerDefault,
  loaderStylesSpinnerTag,
  Search,
  TextField,
  useClassNames,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import { default as SchemaForm } from '@rjsf/core';
import Form from '@rjsf/fluent-ui';
import { RJSFSchema, WidgetProps } from '@rjsf/utils';
import { UiSchema } from '@rjsf/utils';
import { customizeValidator } from '@rjsf/validator-ajv8';
import Ajv2020 from 'ajv/dist/2020';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { permissionBindingsParam, usePermissions } from '../../authz/providers/PermissionsProvider/PermissionsProvider';
import { useUsers } from '../../authz/providers/UsersProvider';
import { useQueryMlopsApiVersion } from '../../discovery-service/discoveryService.hooks';
import { CompletionStats, Job, Job_State } from '../../mlops/gen/ai/h2o/mlops/batch/v1/job_pb';
import { CreateJobRequest } from '../../mlops/gen/ai/h2o/mlops/batch/v1/job_service_pb';
import { SourceSpec } from '../../mlops/gen/ai/h2o/mlops/batch/v1/source_spec_pb';
import { DeploymentComposition } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_composition_pb';
import { KubernetesResourceSpec } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/kubernetes_resource_spec_pb';
import { useMLOpsService } from '../../mlops/hooks';
import { useQueryMonitoringConfig } from '../../mlops/mlops.deployer.hooks';
import { Secret, Secret_State } from '../../secure-store/gen/ai/h2o/securestore/v1/secret_pb';
import { useQuerySecrets, useRevealSecretVersionValue } from '../../secure-store/secure-store.secrets.hooks';
import { useCloudPlatformDiscovery } from '../../utils/hooks';
import { decodeBytes, formatDefaultDateAndTime, formatError } from '../../utils/utils';
import { calculateDuration, MinimalTag, Tag } from '../Orchestrator/WorkflowTabExecutions';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { RoutePaths } from '../Routes';
import { getRandomAdjective } from './adjectives';
import { FormSection, getMonitoringStateProps, getStateProps } from './BatchScoringDetail';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { ENDPOINTS, ROUTES } from './constants';
import { capitalize, DropdownOption } from './DeploymentTabDetail';
import { DeploymentOptions } from './Models';
import ModelSelector, { Expander, TooltipLabel } from './ModelSelector';
import Monitoring, { MonitoringOptionsFixed } from './Monitoring';
import { useGranularMLOpsPermissions } from './userActions';

// Use ajv 2020 for JSON schema validation instead of default ajv 2019.
const validator = customizeValidator({ AjvClass: Ajv2020 });

const uiSchema: UiSchema | any = {
  'ui:globalOptions': {
    duplicateKeySuffixSeparator: '_',
    enableMarkdownInDescription: true,
  },
  environmentalVariables: {
    additionalProperties: {
      'ui:title': 'Value',
    },
  },
};

const OUTPUT_BUFFER_SIZE = 250;

interface IBatchScoringTabDetailStyles {
  form: IStyle;
  metadata: IStyle;
  formEditor?: IStyle;
  dialogInput?: IStyle;
  tooltip?: IStyle;
  infoLabel?: IStyle;
  formSectionWrapper?: IStyle;
  formSection?: IStyle;
  formSectionTitle?: IStyle;
  sourceSinkContainer?: IStyle;
  codeblock?: IStyle;
  completionStatusTable?: IStyle;
  monitoringStatusContainer?: IStyle;
}

interface ISecretSearchWidgetStyles {
  searchBoxToButton: IStyle;
}

export interface IBatchScoringTabDetailProps {
  job?: Job;
  isNew: boolean;
  workspaceId: string;
  deployModelVersionId?: string;
}

export type SecretSearchItem = {
  name: string;
  displayName: string;
  description: string;
  key: string;
  iconName: string;
  type: string;
  secret?: Secret;
};

const batchScoringTabDetailStyles = (theme: IH2OTheme): IBatchScoringTabDetailStyles => ({
    form: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      padding: 20,
      margin: '0px 10px',
    },
    metadata: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    formSectionWrapper: { marginTop: 12, marginBottom: 12 },
    formSectionTitle: {
      textTransform: 'uppercase',
      fontSize: 18,
      fontWeight: 600,
      paddingBottom: 6,
      marginTop: 6,
      marginBottom: 6,
    },
    formSection: {
      backgroundColor: theme.semanticColors?.contentBackground,
      borderRadius: 4,
      padding: 16,
      overflowY: 'auto',
    },
    codeblock: {
      maxHeight: 500,
      overflowY: 'auto',
    },
    formEditor: {
      width: '30%',
      minWidth: 360,
      maxWidth: 460,
      outlineWidth: 1,
      outlineStyle: 'solid',
      outlineColor: theme.semanticColors?.inputBorder,
      borderRadius: 4,
      padding: 10,
      '.ms-Label[id*="title"]': {
        fontWeight: 600,
        fontSize: '14px',
      },
      'span:has(.ms-Button--commandBar)': {
        float: 'left !important',
      },
      'button:has(i[data-icon-name="Delete"])': {
        marginTop: 26,
        i: {
          color: theme.semanticColors?.buttonPrimaryText,
        },
        'i:hover': {
          color: theme.semanticColors?.buttonHoverText,
        },
      },
      '.ms-Button--commandBar': {
        color: theme.semanticColors?.buttonPrimaryText,
        backgroundColor: theme.semanticColors?.buttonInlineBackground,
        borderRadius: '4px',
        ':hover': {
          color: theme.semanticColors?.buttonHoverText,
        },
      },
      '.ms-Button--commandBar i': {
        color: theme.semanticColors?.buttonPrimaryText,
      },
      '.ms-Button--commandBar:hover i': {
        color: theme.semanticColors?.buttonHoverText,
      },
      '.ms-TextField-field[readonly]': {
        color: theme.semanticColors?.textDisabled,
      },
      '.field-object': {
        display: 'flex',
        flexDirection: 'column',
      },
      marginTop: 10,
    },
    dialogInput: {
      maxWidth: 460,
      marginRight: 12,
    },
    tooltip: {
      padding: 4,
    },
    infoLabel: {
      display: 'flex',
      alignItems: 'center',
    },
    sourceSinkContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 20,
    },
    completionStatusTable: {
      width: 460,
      borderCollapse: 'collapse',
      '& th, & td': {
        border: `1px solid ${theme.semanticColors?.inputBorder}`,
        padding: '4px 8px',
        textAlign: 'left',
      },
      '& th': {
        backgroundColor: '#f4f4f4',
      },
    },
    monitoringStatusContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginTop: 10,
    },
  }),
  searchBoxStyles = (theme: IH2OTheme): ISecretSearchWidgetStyles => ({
    searchBoxToButton: {
      marginLeft: 8,
      paddingTop: 26,
      '.ms-SearchBox': {
        padding: 0,
        border: `1px solid ${theme.semanticColors?.inputBorder}`,
        ':hover': {
          borderColor: theme.semanticColors?.inputBorder,
        },
        marginTop: 1,
      },
      '.ms-SearchBox-iconContainer': {
        cursor: 'pointer',
      },
      '.ms-SearchBox-field': {
        display: 'none',
      },
    },
  }),
  getListRowStyles = (theme: IH2OTheme) => ({
    details: {
      padding: '0px 20px',
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
    root: {
      borderRadius: 4,
      backgroundColor: theme.semanticColors?.contentBackground,
    },
    contents: {
      paddingTop: 4,
      paddingBottom: 16,
    },
    row: {
      '&:hover': {
        background: 'transparent',
      },
      '.h2o-ListCell-root:first-child': {
        paddingLeft: 20,
      },
      height: 80,
    },
  }),
  getPlaceholderFromSpecs = (spec: SourceSpec): string => {
    if (spec?.supportedLocationPaths && spec.supportedLocationPaths.length > 0) {
      const schemes = spec.supportedLocationPaths.map((path) => {
        return path.replace(/^\^/, '').replace(/\.\+\$$/, '');
      });

      if (schemes.length === 1) {
        return `${schemes[0]}`;
      } else if (schemes.length > 1) {
        const initialSchemes = schemes.slice(0, -1).join(', ');
        const lastScheme = schemes[schemes.length - 1];
        return `${initialSchemes} or ${lastScheme}`;
      } else {
        return '';
      }
    } else {
      return '';
    }
  },
  transformSecretsForWidget = (secrets?: Secret[]): SecretSearchItem[] => {
    if (!secrets) return [];

    return secrets
      .filter((secret) => secret.state === Secret_State.ACTIVE)
      .map((secret) => ({
        name: secret.name!,
        displayName: secret.displayName || secret.name?.split('/').pop() || '',
        description: secret.name!,
        key: secret.name!,
        iconName: 'PermissionsSolid',
        type: 'Secret',
        secret: secret,
      }));
  };

const useCircularBuffer = (maxSize: number) => {
  const [buffer, setBuffer] = React.useState<string[]>([]);

  const addLine = (line: string) => {
    setBuffer((prev) => {
      const newBuffer = [...prev, line];
      if (newBuffer.length > maxSize) newBuffer.shift();
      return newBuffer;
    });
  };

  return { buffer, addLine };
};

const SECRET_PICKER_WIDGET_KEY = 'secretPickerWidget';

const SecretPickerWidget: React.FC<WidgetProps> = (props) => {
  const { id, value, onChange, disabled, readonly, label } = props;
  const theme = useTheme();
  const classNames = useClassNames<ISecretSearchWidgetStyles, ClassNamesFromIStyles<ISecretSearchWidgetStyles>>(
    'secretSearchBox',
    searchBoxStyles(theme)
  );
  const history = useHistory();
  const { ACTIVE_WORKSPACE_NAME } = useWorkspaces();
  const allowedActions = usePermissions(
    ['actions/securestore/**'],
    `${permissionBindingsParam}${ACTIVE_WORKSPACE_NAME}`
  );
  const [isAbleToManageSecrets, isLoadingPermissions] = allowedActions;
  const { addToast } = useToast();
  const { ACTIVE_WORKSPACE_ID } = useWorkspaces();

  const { data, isLoading: isListLoading } = useQuerySecrets(ACTIVE_WORKSPACE_ID || '', {
    enabled: !!ACTIVE_WORKSPACE_ID,
  });
  const revealSecret = useRevealSecretVersionValue();

  const [isRevealLoading, setIsRevealLoading] = React.useState(false);

  const secrets: SecretSearchItem[] = React.useMemo(() => {
    return transformSecretsForWidget(data?.secrets);
  }, [data?.secrets]);

  const isSecretLoading = isListLoading || isRevealLoading;

  const calloutRef = React.useRef<HTMLDivElement | null>(null);
  const [searchText, setSearchText] = React.useState('');

  const handleSecretSelection = React.useCallback(
    async (item: SecretSearchItem) => {
      setIsRevealLoading(true);

      try {
        const secretName = item.key;
        const versionName = `${secretName}/versions/latest`;
        const revealResponse = await revealSecret({ name: versionName });
        const encodedValue = revealResponse?.value;

        if (!encodedValue) {
          throw new Error('Secret value is empty or not provided.');
        }

        const revealedValue = decodeBytes(encodedValue);

        onChange(revealedValue);
      } catch (err) {
        console.error('Failed to reveal secret value:', formatError(err));
        addToast({
          messageBarType: MessageBarType.error,
          message: `Failed to load secret value: ${formatError(err)}`,
        });
        onChange(undefined);
      } finally {
        setIsRevealLoading(false);
      }
    },
    [addToast, onChange, revealSecret]
  );

  const handleTextChange = React.useCallback(
    (_e: any, newValue?: string) => {
      onChange(newValue);
    },
    [onChange]
  );

  const stringValue = typeof value === 'string' ? value : '';
  const isPassword = props.schema.format === 'password' || props.uiSchema?.['ui:options']?.secretField === true;
  const isWriteOnly = props.schema.writeOnly === true;
  const isEditable = !readonly || isWriteOnly;

  const filteredSecrets = React.useMemo(() => {
    const lowerSearchText = searchText.toLowerCase();
    if (!lowerSearchText) return secrets;

    return secrets.filter(
      (s) =>
        s.displayName.toLowerCase().includes(lowerSearchText) || s.description.toLowerCase().includes(lowerSearchText)
    );
  }, [secrets, searchText]);

  const loadingMessage = isListLoading
    ? 'Loading secrets...'
    : isRevealLoading
    ? 'Revealing secret value...'
    : isLoadingPermissions
    ? 'Checking permissions for managing secrets...'
    : undefined;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', maxWidth: 460 }}>
      <div style={{ flexGrow: 1, position: 'relative' }}>
        <TextField
          id={id}
          label={label || props.schema.title}
          value={stringValue}
          onChange={handleTextChange}
          disabled={disabled || !isEditable}
          readOnly={!isEditable || readonly}
          type={isPassword ? 'password' : 'text'}
          canRevealPassword={isPassword && isEditable}
          styles={{ root: { maxWidth: '100%' } }}
        />
      </div>
      {(isSecretLoading || isLoadingPermissions) && (
        <Loader
          title={loadingMessage}
          styles={{
            root: {
              // These styles align the spinner with the Search TextField.
              marginLeft: 8,
              marginTop: 27,
              border: `1px solid ${theme.semanticColors?.inputBorder}`,
              borderRadius: 4,
              padding: 7,
            },
          }}
          size={SpinnerSize.small}
        />
      )}
      <div
        title={loadingMessage}
        className={classNames.searchBoxToButton}
        style={{ display: isSecretLoading ? 'none' : undefined }}
      >
        <Search
          calloutProps={{
            directionalHint: DirectionalHint.bottomCenter,
            isBeakVisible: true,
            dismissOnTargetClick: true,
            ref: calloutRef,
          }}
          searchBoxProps={{
            disabled: disabled || readonly || isSecretLoading || !isAbleToManageSecrets,
            disableAnimation: true,
            placeholder: 'Search secrets...',
          }}
          onSearchTextChange={setSearchText}
          searchResultItems={filteredSecrets}
          hasSearchResult={true}
          onRenderSearchResultItemActions={() => <></>}
          searchResultItemFields={{
            titleField: 'displayName',
            descriptionField: 'description',
            iconNameField: 'iconName',
          }}
          title={isAbleToManageSecrets ? 'Select a secret' : "You don't have permission to use secrets"}
          loadingMessage={loadingMessage}
          onClickItem={(item: any) => {
            handleSecretSelection(item as SecretSearchItem);
            // HACK: Dismiss callout.
            calloutRef.current?.parentElement?.click();
          }}
          actions={
            <Link
              onClick={() => {
                history.push(RoutePaths.SECURE_STORE_SECRETS);
              }}
              style={{ color: 'var(--h2o-gray500, #6B7280)' }}
            >
              Manage secrets
            </Link>
          }
          styles={{
            root: { width: 32 },
          }}
        />
      </div>
    </div>
  );
};

const customWidgets = {
  [SECRET_PICKER_WIDGET_KEY]: SecretPickerWidget,
};

export const BatchScoringTabDetail = ({
  job: defaultJob,
  isNew,
  workspaceId,
  deployModelVersionId,
}: IBatchScoringTabDetailProps) => {
  const theme = useTheme(),
    classNames = useClassNames<IBatchScoringTabDetailStyles, ClassNamesFromIStyles<IBatchScoringTabDetailStyles>>(
      'batchScoringTabDetail',
      batchScoringTabDetailStyles(theme)
    ),
    history = useHistory(),
    { usersNameMap } = useUsers(),
    { addToast } = useToast(),
    { showDialog } = useConfirmDialog(),
    workspaceName = React.useMemo(() => `workspaces/${workspaceId}`, [workspaceId]),
    { buffer, addLine } = useCircularBuffer(OUTPUT_BUFFER_SIZE),
    permissions = useGranularMLOpsPermissions(workspaceName),
    codeBlockRef = React.createRef<HTMLDivElement>(),
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    storageTransport = createConnectTransport({
      baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
    }),
    registeredModelClient = createClient(RegisteredModelService, storageTransport),
    registeredModelVersionClient = createClient(RegisteredModelVersionService, storageTransport),
    mlopsService = useMLOpsService(),
    mlopsApiVersionQuery = useQueryMlopsApiVersion(),
    mlopsApiVersion = mlopsApiVersionQuery.data,
    isMlopsApiV1_0_x = mlopsApiVersion?.startsWith('1.0.'),
    [sourceMimeTypeOptions, setSourceMimeTypeOptions] = React.useState<DropdownOption[]>([]),
    [sinkMimeTypeOptions, setSinkMimeTypeOptions] = React.useState<DropdownOption[]>([]),
    [job, setJob] = React.useState<Job | undefined>(defaultJob),
    [sourceSpecs, setSourceSpecs] = React.useState<SourceSpec[]>(),
    [sourceOptions, setSourceOptions] = React.useState<DropdownOption[]>([]),
    [selectedSourceSpecKey, setSelectedSourceSpecKey] = React.useState(''),
    [completionStats, setCompletionStats] = React.useState<CompletionStats>(),
    [currentSourceSchema, setCurrentSourceSchema] = React.useState<RJSFSchema>(),
    [sourceConfig, setSourceConfig] = React.useState({}),
    [sinkConfig, setSinkConfig] = React.useState({}),
    [sinkSpecs, setSinkSpecs] = React.useState<SourceSpec[]>(),
    [sinkOptions, setSinkOptions] = React.useState<DropdownOption[]>([]),
    [selectedSinkSpecKey, setSelectedSinkSpecKey] = React.useState(''),
    [sourcePlaceholder, setSourcePlaceholder] = React.useState<string>(''),
    [sinkPlaceholder, setSinkPlaceholder] = React.useState<string>(''),
    [currentSinkSchema, setCurrentSinkSchema] = React.useState<RJSFSchema>(),
    [affinityFromSelector, setAffinityFromSelector] = React.useState<string>('none'),
    [tolerationFromSelector, setTolerationFromSelector] = React.useState<string>('none'),
    [isModelSelectorAdvancedExpanded, setIsModelSelectorAdvancedExpanded] = React.useState<boolean>(false),
    [isAdvancedMonitoringExpanded, setAdvancedMonitoringExpanded] = React.useState(false),
    isBatchScoreModel = React.useMemo(() => !!deployModelVersionId, [deployModelVersionId]),
    [jobName, setJobName] = React.useState<string>(
      isNew && !isBatchScoreModel ? `${capitalize(getRandomAdjective())} job` : ''
    ),
    [jobOutput, setJobOutput] = React.useState<string>(),
    [showJobOutput, setShowJobOutput] = React.useState(false),
    [defaultSelectedModelIds, setDefaultSelectedModelIds] = React.useState<string[]>([]),
    [defaultSelectedExperimentIds, setDefaultSelectedExperimentIds] = React.useState<string[]>([]),
    [defaultKubernetesResourceSpec, setDefaultKubernetesResourceSpec] = React.useState<KubernetesResourceSpec>(),
    [defaultEnvVariablesConfig, setDefaultEnvVariablesConfig] = React.useState<Record<string, string>>(),
    [selectedModels, setSelectedModels] = React.useState<(PlainMessage<RegisteredModel> | undefined)[]>([]),
    [defaultDeploymentComposition, setDefaultDeploymentComposition] = React.useState<DeploymentComposition>(),
    [deploymentCompositionOptions, setDeploymentCompositionOptions] = React.useState<
      Partial<DeploymentOptions> | undefined
    >(undefined),
    [idField, setIdField] = React.useState<string>(``),
    [inputFeaturesSelectedKey, setInputFeaturesSelectedKey] = React.useState<string>('none'),
    [sourceLocation, setSourceLocation] = React.useState<string>(''),
    [sinkLocation, setSinkLocation] = React.useState<string>(''),
    [selectedSourceMimeTypeKey, setSelectedSourceMimeTypeKey] = React.useState(''),
    [selectedSinkMimeTypeKey, setSelectedSinkMimeTypeKey] = React.useState(''),
    [miniBatchSize, setMiniBatchSize] = React.useState<number>(),
    [isMonitoringEnabled, setIsMonitoringEnabled] = React.useState<boolean>(false),
    [monitoringOptions, setMonitoringOptions] = React.useState<MonitoringOptionsFixed | undefined>(undefined),
    [noMonitoringColumnsSpecified, setNoMonitoringColumnsSpecified] = React.useState(true),
    // TODO: Handle loading.
    [, setLoading] = React.useState(true),
    [isWidgetLoading, setIsWidgetLoading] = React.useState(true),
    [isSubmitDisabled, setIsSubmitDisabled] = React.useState(false),
    [showSourceConfigValidation, setShowSourceConfigValidation] = React.useState(false),
    [showSinkConfigValidation, setShowSinkConfigValidation] = React.useState(false),
    [widgetRowActionsDisabled, setWidgetRowActionsDisabled] = React.useState(false),
    sourceFormRef = React.createRef<SchemaForm>(),
    sinkFormRef = React.createRef<SchemaForm>(),
    [isStreaming, setIsStreaming] = React.useState(false),
    isTheNameAutoSet = React.useRef(false),
    loadStateRef = React.useRef({
      createJob: false,
      fetchSourceSpecs: false,
      fetchSinkSpecs: false,
      fetchingDefaultRuntimeKubernetesResourceSpec: false,
      fetchingKubernetesConfigurationShortcuts: false,
      fetchModelVersionForExperiment: false,
      cancellingJob: false,
      deletingJob: false,
    }),
    evaluateLoading = () => {
      if (
        !loadStateRef.current.createJob &&
        !loadStateRef.current.fetchSourceSpecs &&
        !loadStateRef.current.fetchSinkSpecs &&
        !loadStateRef.current.fetchingDefaultRuntimeKubernetesResourceSpec &&
        !loadStateRef.current.fetchingKubernetesConfigurationShortcuts &&
        !loadStateRef.current.fetchModelVersionForExperiment &&
        !loadStateRef.current.cancellingJob &&
        !loadStateRef.current.deletingJob
      ) {
        setLoading(false);
      }
    },
    experimentId =
      deploymentCompositionOptions?.firstModelDeploymentComposition?.experimentId ||
      defaultDeploymentComposition?.experimentId,
    isExistingMonitoring = Boolean(!isNew && job?.monitoringOptions !== null && experimentId),
    onChangeMonitoringOptions = React.useCallback((options: MonitoringOptionsFixed) => {
      const enabledColumns = options?.columns?.filter((column) => !column?.isColumnDisabled);
      setNoMonitoringColumnsSpecified((enabledColumns || []).length === 0);
      setMonitoringOptions(options);
    }, []),
    getRegisteredModel = React.useCallback(
      async (modelId: string) => {
        try {
          const response = await registeredModelClient.getRegisteredModel({
            modelId,
          });
          return (response as GetRegisteredModelResponse).registeredModel;
        } catch (err) {
          const message = `Failed to fetch registered model: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        }
      },
      [addToast]
    ),
    pickModelVersionFromCurrenWorkspace = React.useCallback(
      async (modelVersions: RegisteredModelVersion[]) => {
        const versionsCount = modelVersions?.length || 0;
        if (versionsCount === 1) {
          return modelVersions?.[0];
        } else if (versionsCount > 1) {
          let version = 0;
          while (version < versionsCount) {
            const model = await getRegisteredModel(modelVersions?.[version]?.registeredModelId || '');
            if (`workspaces/${model?.projectId}` === workspaceName) {
              return modelVersions?.[version];
            }
            version++;
          }
          return undefined;
        } else {
          return undefined;
        }
      },
      [workspaceName]
    ),
    fetchModelVersionForExperiment = React.useCallback(
      async (experimentId: string) => {
        loadStateRef.current.fetchModelVersionForExperiment = true;
        setIsWidgetLoading(true);
        try {
          const response = await registeredModelVersionClient.getModelVersionForExperiment(
            new GetModelVersionForExperimentRequest({ experimentId })
          );
          if (response && !response.registeredModelVersion) {
            console.error('No registered model version found in the response.');
          }
          const modelVersion = await pickModelVersionFromCurrenWorkspace(
            (response as GetModelVersionForExperimentResponse)?.registeredModelVersion || []
          );
          if (modelVersion) {
            setDefaultSelectedModelIds([modelVersion.registeredModelId || '']);
            setDefaultSelectedExperimentIds([modelVersion.experimentId || '']);
          }
        } catch (err) {
          const message = `Failed to fetch model version for experiment: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.fetchModelVersionForExperiment = false;
          evaluateLoading();
        }
      },
      [registeredModelVersionClient, addToast, setDefaultSelectedModelIds, setDefaultSelectedExperimentIds]
    ),
    createJob = React.useCallback(async () => {
      if (!deploymentCompositionOptions?.firstModelDeploymentComposition) return;
      loadStateRef.current.createJob = true;
      setLoading(true);
      setIsSubmitDisabled(true);

      try {
        const enabledColumns = monitoringOptions?.columns?.filter((column) => !column?.isColumnDisabled);
        const enabledBaselineAggregations = monitoringOptions?.baselineAggregations?.filter((aggregation) =>
          enabledColumns?.some((column) => column.column === aggregation.column)
        );

        const monitoringOptionsPayload = !isMonitoringEnabled
          ? undefined
          : {
              ...monitoringOptions,
              enabled: isMonitoringEnabled,
              columns: enabledColumns,
              baselineAggregations: enabledBaselineAggregations,
            };

        const createJobBody: CreateJobRequest = {
          parent: workspaceName || '',
          job: {
            displayName: jobName,
            source: {
              spec: selectedSourceSpecKey,
              config: JSON.stringify(sourceConfig),
              location: sourceLocation,
              mimeType: selectedSourceMimeTypeKey,
            },
            sink: {
              spec: selectedSinkSpecKey,
              config: JSON.stringify(sinkConfig),
              location: sinkLocation,
              mimeType: selectedSinkMimeTypeKey,
            },
            instanceSpec: {
              deploymentComposition: {
                ...deploymentCompositionOptions.firstModelDeploymentComposition,
                artifactType: deploymentCompositionOptions.firstModelDeploymentComposition.deployableArtifactType,
                artifactProcessor: deploymentCompositionOptions.firstModelDeploymentComposition.artifactProcessor,
                runtime: deploymentCompositionOptions.firstModelDeploymentComposition.runtime,
              },
              resourceSpec: {
                resourceRequirement: {
                  requests: deploymentCompositionOptions.requests,
                  limits: deploymentCompositionOptions.limits,
                },
                replicas: deploymentCompositionOptions.kubernetesOptions?.replicas,
                minimalAvailableReplicas: undefined,
              },
              kubernetesConfigShortcut: {
                kubernetesAffinityShortcut: affinityFromSelector === 'none' ? '' : affinityFromSelector || '',
                kubernetesTolerationShortcut: tolerationFromSelector === 'none' ? '' : tolerationFromSelector || '',
              },
              environmentVariables: deploymentCompositionOptions.envVariables,
            },
            batchParameters: {
              miniBatchSize,
            },
            modelRequestParameters:
              inputFeaturesSelectedKey === 'custom'
                ? { idField }
                : inputFeaturesSelectedKey === 'all'
                ? { includeInputFeatures: true }
                : undefined,
            monitoringOptions: monitoringOptionsPayload,
          },
        };

        if (idField) {
          if (!createJobBody.job.modelRequestParameters) {
            createJobBody.job.modelRequestParameters = {};
          }
          createJobBody.job.modelRequestParameters.idField = idField;
        }

        await mlopsService.createJob(createJobBody);
        history.push(`/mlops/${workspaceName}${ROUTES.BATCH_SCORING}`);
      } catch (err) {
        const message = `Failed to create job: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      } finally {
        loadStateRef.current.createJob = false;
        evaluateLoading();
        setIsSubmitDisabled(false);
      }
    }, [
      workspaceName,
      permissions,
      mlopsService,
      jobName,
      deploymentCompositionOptions,
      sourceConfig,
      sourceSpecs,
      selectedSourceMimeTypeKey,
      sourceLocation,
      sinkConfig,
      sinkSpecs,
      selectedSinkMimeTypeKey,
      sinkLocation,
      affinityFromSelector,
      tolerationFromSelector,
      idField,
      inputFeaturesSelectedKey,
      miniBatchSize,
      selectedSourceSpecKey,
      selectedSinkSpecKey,
      history,
      addToast,
      isMonitoringEnabled,
      monitoringOptions,
    ]),
    listSourceSpecs = React.useCallback(async () => {
      loadStateRef.current.fetchSourceSpecs = true;
      setLoading(true);

      try {
        const data = await mlopsService.listSourceSpecs({});
        if (data && !data.sourceSpecs) {
          throw new Error('No source specs found in the response.');
        }
        setSourceSpecs(data.sourceSpecs);
        setSourceOptions(
          (data.sourceSpecs || []).map(
            (sourceSpec) =>
              ({
                key: sourceSpec.name,
                text: sourceSpec.displayName,
                data: sourceSpec,
              } as DropdownOption)
          )
        );
        if (isNew) {
          setSelectedSourceSpecKey(data.sourceSpecs?.[0]?.name || '');
          setSourceMimeTypeOptions(
            data.sourceSpecs?.[0]?.supportedMimeTypes?.map(
              (mimeType) =>
                ({
                  key: mimeType,
                  text: mimeType,
                } as DropdownOption)
            ) || []
          );
        }
      } catch (err) {
        const message = `Failed to fetch source specs: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      } finally {
        loadStateRef.current.fetchSourceSpecs = false;
        evaluateLoading();
      }
    }, [mlopsService, isNew]),
    listSinkSpecs = React.useCallback(async () => {
      loadStateRef.current.fetchSinkSpecs = true;
      setLoading(true);

      try {
        const data = await mlopsService.listSinkSpecs({});
        if (data && !data.sinkSpecs) {
          throw new Error('No sink specs found in the response.');
        }
        setSinkSpecs(data.sinkSpecs);
        setSinkOptions(
          (data.sinkSpecs || []).map(
            (sinkSpec) =>
              ({
                key: sinkSpec.name,
                text: sinkSpec.displayName,
                data: sinkSpec,
              } as DropdownOption)
          )
        );
        if (isNew) {
          setSelectedSinkSpecKey(data.sinkSpecs?.[0]?.name || '');
          setSinkMimeTypeOptions(
            data.sinkSpecs?.[0]?.supportedMimeTypes?.map(
              (mimeType) =>
                ({
                  key: mimeType,
                  text: mimeType,
                } as DropdownOption)
            ) || []
          );
        }
      } catch (err) {
        const message = `Failed to fetch sink specs: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      } finally {
        loadStateRef.current.fetchSinkSpecs = false;
        evaluateLoading();
      }
    }, [mlopsService, isNew]),
    cancelJob = React.useCallback(
      async (id: string) => {
        loadStateRef.current.cancellingJob = true;
        setWidgetRowActionsDisabled(true);
        try {
          await mlopsService.cancelJob({ name: id });
          // Refresh job data after cancellation
          window.location.reload();
        } catch (err) {
          const message = `Failed to cancel job: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.cancellingJob = false;
          evaluateLoading();
          setWidgetRowActionsDisabled(false);
        }
      },
      [mlopsService, addToast]
    ),
    deleteJob = React.useCallback(
      async (id: string) => {
        loadStateRef.current.deletingJob = true;
        setWidgetRowActionsDisabled(true);
        try {
          await mlopsService.deleteJob({ name: id });
          history.push(`/mlops/${workspaceName}${ROUTES.BATCH_SCORING}`);
        } catch (err) {
          const message = `Failed to delete job: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.deletingJob = false;
          evaluateLoading();
          setWidgetRowActionsDisabled(false);
        }
      },
      [mlopsService]
    ),
    streamJobOutput = React.useCallback(
      async (id: string) => {
        setIsStreaming(true);
        const response = await mlopsService.getJobOutput({ name: id });
        if (!response?.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          let boundary;
          while ((boundary = buffer.indexOf('\n')) >= 0) {
            const chunk = buffer.slice(0, boundary).trim();
            buffer = buffer.slice(boundary + 1);

            if (chunk) {
              try {
                const jobOutput = JSON.parse(chunk);
                addLine(`${jobOutput.result.line}\n${jobOutput.result.error}`);
              } catch (err) {
                console.error('Failed to parse job output:', err);
              }
            }
          }
        }
        setIsStreaming(false);
      },
      [mlopsService]
    ),
    onActionClick = () => {
      if (
        !jobName ||
        !selectedSourceSpecKey ||
        !sourceConfig ||
        !selectedSinkSpecKey ||
        !sinkConfig ||
        !selectedSourceMimeTypeKey ||
        !sourceLocation ||
        !selectedSinkMimeTypeKey ||
        !sinkLocation ||
        (currentSourceSchema && !validator.isValid(currentSourceSchema, sourceConfig, {}))
      ) {
        setShowSourceConfigValidation(true);
        setShowSinkConfigValidation(true);
        return;
      }
      void createJob();
    };

  const monitoringConfigQuery = useQueryMonitoringConfig({
    meta: {
      toastError: (err: unknown) => `Failed to fetch monitoring config: ${formatError(err)}`,
    },
  });
  const monitoringConfig = monitoringConfigQuery.data?.monitoringConfig;
  const isMonitoringGloballyDisabled = !monitoringConfig?.enabled;

  const listRowColumns = React.useMemo(
    () => [
      {
        data: {
          headerFieldName: 'displayName',
          listCellProps: {
            emptyMessage: 'No Description',
            onRenderHeader: ({ displayName }: any) => (
              <h3 style={{ padding: 0, margin: 0 }}>{`Execution of ${displayName}`}</h3>
            ),
          },
        },
        key: 'title',
        fieldName: 'createdBy',
        maxWidth: 700,
        minWidth: 400,
        name: 'Title',
      },
      {
        key: 'state',
        fieldName: 'state',
        maxWidth: 200,
        minWidth: 140,
        name: 'State',
        data: {
          listCellProps: {
            onRenderText: ({ state }: any) => {
              return state === Job_State.RUNNING ? (
                <Loader
                  label="Running"
                  styles={[loaderStylesSpinnerDefault, loaderStylesSpinnerTag, { root: { position: 'relative' } }]}
                />
              ) : (
                <Tag title={getStateProps(state).name} color={getStateProps(state).color} />
              );
            },
          },
        },
      },
      {
        key: 'startTime',
        fieldName: 'startTimeLocal',
        maxWidth: 200,
        minWidth: 130,
        name: 'Started at',
      },
      {
        key: 'endTime',
        fieldName: 'endTimeLocal',
        maxWidth: 200,
        minWidth: 130,
        name: 'Ended at',
      },
      {
        key: 'duration',
        fieldName: 'duration',
        maxWidth: 240,
        minWidth: 180,
        name: 'Duration',
      },
      {
        key: 'buttons',
        name: '',
        maxWidth: 260,
        minWidth: 180,
        data: {
          listCellProps: {
            onRenderText: ({ onCancel, onDeleteJob, state, permissions, actionsDisabled }: any) => (
              <>
                {state !== Job_State.CANCELLED &&
                state !== Job_State.CANCELLING &&
                state !== Job_State.FAILED &&
                state !== Job_State.FINISHED &&
                state !== Job_State.SUBMISSION_FAILED &&
                state !== Job_State.TIMEOUT &&
                state !== Job_State.STATE_UNSPECIFIED &&
                permissions?.isAbleToCancelBatchScoringJobs ? (
                  <Button
                    text="Cancel"
                    onClick={onCancel}
                    style={{ color: 'var(--h2o-yellow700)', borderColor: 'var(--h2o-yellow700)' }}
                    iconProps={{ iconName: 'Cancel', style: { color: 'var(--h2o-yellow700)' } }}
                    disabled={actionsDisabled}
                  />
                ) : null}
                {(state === Job_State.CANCELLED ||
                  state === Job_State.FAILED ||
                  state === Job_State.FINISHED ||
                  state === Job_State.SUBMISSION_FAILED ||
                  state === Job_State.TIMEOUT) &&
                permissions?.isAbleToDeleteBatchScoringJobs ? (
                  <Button
                    text="Delete"
                    onClick={onDeleteJob}
                    style={{
                      color: 'var(--h2o-red400)',
                      borderColor: 'var(--h2o-red400)',
                    }}
                    disabled={actionsDisabled}
                    iconProps={{ iconName: 'Delete', style: { color: 'var(--h2o-red400)' } }}
                  />
                ) : null}
              </>
            ),
          },
        },
      },
    ],
    []
  );

  React.useEffect(() => {
    if (!isNew && job?.state === Job_State.RUNNING && !isStreaming && showJobOutput) streamJobOutput(job?.name || '');
  }, [job?.state, job?.name, isNew, streamJobOutput, isStreaming, showJobOutput]);

  React.useEffect(() => {
    if (!isStreaming) return;
    const interval = setInterval(() => {
      if (buffer) {
        setJobOutput(buffer.join('\n'));
        if (codeBlockRef.current) {
          codeBlockRef.current.scrollTop = codeBlockRef.current.scrollHeight;
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [buffer, isStreaming, showJobOutput]);

  React.useEffect(() => {
    if (isTheNameAutoSet.current) return;
    if (isNew && isBatchScoreModel && selectedModels?.[0]) {
      setJobName(`${selectedModels?.[0]?.displayName} job` || '');
      isTheNameAutoSet.current = true;
    }
  }, [isNew, isBatchScoreModel, selectedModels]);

  React.useEffect(() => {
    void listSourceSpecs();
    void listSinkSpecs();
  }, [listSourceSpecs, listSinkSpecs]);

  React.useEffect(() => {
    setCurrentSourceSchema(
      JSON.parse(sourceSpecs?.find((spec) => spec.name === selectedSourceSpecKey)?.schema || '{}')
    );
  }, [selectedSourceSpecKey, sourceSpecs]);

  React.useEffect(() => {
    setCurrentSinkSchema(JSON.parse(sinkSpecs?.find((spec) => spec.name === selectedSinkSpecKey)?.schema || '{}'));
  }, [selectedSinkSpecKey, sinkSpecs]);

  // Inject Custom Widget logic into schema dynamically.
  const getCustomUiSchema = React.useCallback((currentSchema: RJSFSchema) => {
    const newUiSchema = { ...uiSchema };
    if (!currentSchema?.properties) return newUiSchema;

    for (const key of Object.keys(currentSchema.properties)) {
      const prop = currentSchema.properties[key] as any;
      // TODO: Type the prop object properly.
      const isWriteOnly = !!(prop && typeof prop === 'object' && prop.writeOnly === true);

      if (isWriteOnly) {
        if (!newUiSchema[key]) {
          newUiSchema[key] = {};
        }

        // Set custom widget for picker.
        newUiSchema[key]['ui:widget'] = SECRET_PICKER_WIDGET_KEY;

        // Mark as password field for the custom widget to display dots.
        if (isWriteOnly) {
          newUiSchema[key]['ui:options'] = {
            ...newUiSchema[key]['ui:options'],
            secretField: true,
          };
        }
      }
    }
    return newUiSchema;
  }, []);

  React.useEffect(() => {
    if (sourceFormRef?.current) sourceFormRef.current?.submit();
  }, [currentSourceSchema, sourceFormRef.current]);

  React.useEffect(() => {
    if (sinkFormRef?.current) sinkFormRef.current?.submit();
  }, [currentSinkSchema, sinkFormRef.current]);

  React.useEffect(() => {
    const selectedSourceSpec = sourceSpecs?.find((spec) => spec.name === selectedSourceSpecKey);
    if (selectedSourceSpec) setSourcePlaceholder(getPlaceholderFromSpecs(selectedSourceSpec));
  }, [sourceSpecs, selectedSourceSpecKey]);

  React.useEffect(() => {
    const selectedSinkSpec = sinkSpecs?.find((spec) => spec.name === selectedSinkSpecKey);
    if (selectedSinkSpec) setSinkPlaceholder(getPlaceholderFromSpecs(selectedSinkSpec));
  }, [sinkSpecs, selectedSinkSpecKey]);

  React.useEffect(() => {
    if (!selectedModels?.[0]) {
      setAdvancedMonitoringExpanded(false);
    }
  }, [selectedModels]);

  React.useEffect(() => {
    if (defaultJob) {
      setJob(defaultJob);
      setJobName(defaultJob?.displayName || '');
      setSelectedSourceSpecKey(defaultJob?.source?.spec || '');
      setSelectedSinkSpecKey(defaultJob?.sink?.spec || '');
      setSourceConfig(JSON.parse(defaultJob?.source?.config || '{}'));
      setSinkConfig(JSON.parse(defaultJob?.sink?.config || '{}'));
      setSourceLocation(defaultJob?.source?.location || '');
      setSinkLocation(defaultJob?.sink?.location || '');
      setSelectedSourceMimeTypeKey(defaultJob?.source?.mimeType || '');
      setInputFeaturesSelectedKey(
        defaultJob?.modelRequestParameters?.idField
          ? 'custom'
          : defaultJob?.modelRequestParameters?.includeInputFeatures
          ? 'all'
          : 'none'
      );
      if (defaultJob?.modelRequestParameters?.idField) {
        setIdField(defaultJob?.modelRequestParameters?.idField);
      }
      setSourceMimeTypeOptions(
        sourceSpecs
          ?.find((spec) => spec.name === defaultJob?.source?.spec)
          ?.supportedMimeTypes?.map(
            (mimeType: string) =>
              ({
                key: mimeType,
                text: mimeType,
              } as DropdownOption)
          ) || []
      );
      if (defaultJob?.state === Job_State.FINISHED) setCompletionStats(defaultJob?.completionStats);
      setSelectedSinkMimeTypeKey(defaultJob?.sink?.mimeType || '');
      setSinkMimeTypeOptions(
        sinkSpecs
          ?.find((spec) => spec.name === defaultJob?.sink?.spec)
          ?.supportedMimeTypes?.map(
            (mimeType) =>
              ({
                key: mimeType,
                text: mimeType,
              } as DropdownOption)
          ) || []
      );
      setDefaultKubernetesResourceSpec(defaultJob?.instanceSpec?.resourceSpec);
      setDefaultEnvVariablesConfig(defaultJob?.instanceSpec?.environmentVariables || {});
      setAffinityFromSelector(defaultJob?.instanceSpec?.kubernetesConfigShortcut?.kubernetesAffinityShortcut || 'none');
      setTolerationFromSelector(
        defaultJob?.instanceSpec?.kubernetesConfigShortcut?.kubernetesTolerationShortcut || 'none'
      );
      setIsMonitoringEnabled(!!defaultJob?.monitoringOptions?.enabled);
      setMonitoringOptions(defaultJob?.monitoringOptions as MonitoringOptionsFixed);

      setMiniBatchSize(defaultJob?.batchParameters?.miniBatchSize || 64);
      setDefaultDeploymentComposition({
        experimentId: defaultJob?.instanceSpec?.deploymentComposition?.experimentId || '',
        artifactId: '',
        deployableArtifactType: defaultJob?.instanceSpec?.deploymentComposition?.artifactType || '',
        artifactProcessor: defaultJob?.instanceSpec?.deploymentComposition?.artifactProcessor || '',
        runtime: defaultJob?.instanceSpec?.deploymentComposition?.runtime || '',
        runtimeImage: defaultJob?.instanceSpec?.deploymentComposition?.runtimeImage || '',
      } as DeploymentComposition);
      setDefaultSelectedExperimentIds([defaultJob?.instanceSpec?.deploymentComposition?.experimentId || '']);

      fetchModelVersionForExperiment(defaultJob?.instanceSpec?.deploymentComposition?.experimentId || '');
    }
  }, [defaultJob, sourceSpecs, sinkSpecs, workspaceName]);

  const monitoringStateProps = getMonitoringStateProps(job?.monitoringState);

  return (
    <div className={classNames.form}>
      {job?.state ? (
        <>
          <ListRow
            columns={listRowColumns}
            data={{
              ...job,
              status: getStateProps(job?.state).name,
              startTimeLocal: job?.startTime ? formatDefaultDateAndTime(job?.startTime) : 'N/A',
              endTimeLocal: job?.endTime ? formatDefaultDateAndTime(job?.endTime) : 'N/A',
              duration: calculateDuration(job?.startTime, job?.endTime),
              createdBy: `Created by ${usersNameMap?.[job?.creator || '']?.displayName || job?.creator}`,
              permissions,
              onCancel: () => void cancelJob(job?.name || ''),
              onDeleteJob: () => {
                showDialog?.({
                  title: 'Delete job?',
                  onConfirm: () => {
                    void deleteJob(job?.name || '');
                  },
                });
              },
              actionsDisabled: widgetRowActionsDisabled,
            }}
            contents={
              <>
                {!isNew && job?.monitoringState ? (
                  <div className={classNames.monitoringStatusContainer}>
                    <b>Monitoring status: </b>
                    <MinimalTag title={monitoringStateProps.name} color={monitoringStateProps.color} />
                  </div>
                ) : null}
                {!isNew && job?.state === Job_State.FINISHED ? (
                  <>
                    {isMonitoringEnabled ? <div style={{ height: 10 }}></div> : null}
                    <div>
                      <table className={classNames.completionStatusTable}>
                        <thead>
                          <tr>
                            <th>Processed rows</th>
                            <th>Error rows</th>
                            <th>Finished pipelines</th>
                            <th>Failed pipelines</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{completionStats?.processedRows?.toString()}</td>
                            <td>{completionStats?.errorRowsCount?.toString()}</td>
                            <td>{completionStats?.finishedPipelineCount?.toString()}</td>
                            <td>
                              <TooltipLabel
                                label={`${completionStats?.failedPipelines?.length || 0}`}
                                tooltipText={`${
                                  completionStats?.failedPipelines?.join(', ') || 'No failed pipelines.'
                                }`}
                                hasTopSpacing={false}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : null}
                {!isNew && job?.state === Job_State.RUNNING ? (
                  <>
                    <Button
                      iconName="ChevronRight"
                      text="Job output"
                      onClick={(ev) => {
                        const icon = (ev.currentTarget as HTMLElement)?.querySelector('.ms-Icon');
                        if (icon) {
                          setShowJobOutput(!icon.classList?.contains('rotate90'));
                          icon.classList.toggle('rotate90');
                        }
                      }}
                      styles={[
                        buttonStylesStealth,
                        {
                          icon: {
                            fontSize: 14,
                            transitionDuration: '300ms',
                            transitionProperty: 'transform',
                          },
                          root: {
                            marginTop: 10,
                            '.ms-Icon.rotate90': {
                              transform: 'rotate(90deg)',
                            },
                            backgroundColor: 'transparent',
                          },
                        },
                      ]}
                    />
                    {showJobOutput ? (
                      <>
                        <p>{`Output is limited to last ${OUTPUT_BUFFER_SIZE} lines.`}</p>
                        <div className={classNames.codeblock} ref={codeBlockRef}>
                          <CodeBlock>{jobOutput || 'Streaming output...'}</CodeBlock>
                        </div>
                      </>
                    ) : null}
                  </>
                ) : null}
              </>
            }
            styles={getListRowStyles(theme)}
          />
          <div style={{ height: 25 }}></div>
        </>
      ) : null}
      <TextField
        label="Job name"
        required
        onChange={(_ev, value) => {
          setJobName(value || '');
        }}
        value={jobName}
        disabled={!isNew || !permissions?.isAbleToCreateBatchScoringJobs}
        styles={{ root: { maxWidth: 360 } }}
      />
      <FormSection title="Model">
        <ModelSelector
          selectedModels={selectedModels}
          onSelectedModelsChange={setSelectedModels}
          defaultSelectedVersionId={isBatchScoreModel ? deployModelVersionId : undefined}
          defaultSelectedModelIds={defaultSelectedModelIds}
          defaultSelectedExperimentIds={defaultSelectedExperimentIds}
          defaultDeploymentCompositionA={defaultDeploymentComposition}
          defaultRequestsConfig={defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.requests}
          defaultLimitsConfig={defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.limits}
          defaultReplicas={defaultKubernetesResourceSpec?.replicas}
          defaultEnvVariablesConfig={defaultEnvVariablesConfig}
          defaultAffinity={job?.instanceSpec?.kubernetesConfigShortcut?.kubernetesAffinityShortcut || 'none'}
          defaultToleration={job?.instanceSpec?.kubernetesConfigShortcut?.kubernetesTolerationShortcut || 'none'}
          onAffinityChange={setAffinityFromSelector}
          onTolerationChange={setTolerationFromSelector}
          deploymentType={'single_model'}
          onChangeOptions={setDeploymentCompositionOptions}
          onContentLoadStart={() => setIsWidgetLoading(true)}
          onContentLoadFinish={() => setIsWidgetLoading(false)}
          isNew={isNew}
          readOnly={!isNew || !permissions?.isAbleToCreateBatchScoringJobs}
          workspaceId={workspaceId}
          onChangeModelSelectorAdvancedExpanded={setIsModelSelectorAdvancedExpanded}
        />
        {selectedModels?.[0] && isModelSelectorAdvancedExpanded ? (
          <div style={{ marginLeft: 31, marginTop: 8 }}>
            <TextField
              type="number"
              label="Batch size"
              value={`${miniBatchSize}`}
              placeholder="64"
              onChange={(_ev, value) => {
                setMiniBatchSize(parseInt(value || '0'));
              }}
              className={classNames.dialogInput}
              styles={{ field: { paddingRight: 0 }, root: { maxWidth: 360 } }}
              aria-labelledby="max-queue-field"
              readOnly={!isNew || !permissions?.isAbleToCreateBatchScoringJobs}
              onRenderLabel={(props?: ITextFieldProps) => (
                <div id={'max-queue-field'} className={classNames.infoLabel}>
                  {props?.label}
                  <Info isTooltip className={classNames.tooltip}>
                    The number of the records send in one request to scorer. If not provided 64 will be used as default.
                  </Info>
                </div>
              )}
            />
          </div>
        ) : null}
      </FormSection>
      <div style={{ height: 10 }}></div>
      <div className={classNames.sourceSinkContainer}>
        <div>
          <FormSection title="Source">
            <Dropdown
              label="Source spec"
              selectedKey={selectedSourceSpecKey}
              onChange={(_ev, option) => {
                setSelectedSourceSpecKey(`${option?.key}`);
                setSourceMimeTypeOptions(
                  option?.data?.supportedMimeTypes?.map(
                    (mimeType: string) =>
                      ({
                        key: mimeType,
                        text: mimeType,
                      } as DropdownOption)
                  )
                );
              }}
              options={sourceOptions}
              disabled={!isNew || !permissions?.isAbleToCreateBatchScoringJobs}
              styles={{ root: { maxWidth: 360 } }}
            />
            {selectedSourceSpecKey && currentSourceSchema ? (
              <Form
                ref={sourceFormRef}
                id={'executor-pool-config-form'}
                idPrefix="source_spec_"
                validator={validator}
                schema={currentSourceSchema}
                formData={sourceConfig}
                onChange={(schema) => {
                  setSourceConfig(schema.formData);
                }}
                liveValidate={isNew}
                showErrorList={showSourceConfigValidation ? 'bottom' : false}
                readonly={!permissions?.isAbleToCreateBatchScoringJobs || !isNew}
                className={classNames.formEditor}
                uiSchema={getCustomUiSchema(currentSourceSchema)}
                widgets={customWidgets}
              >
                <></>
              </Form>
            ) : null}
            <div style={{ height: 8 }} />
            <Dropdown
              label="Source MIME type"
              required
              selectedKey={selectedSourceMimeTypeKey}
              onChange={(_ev, option) => {
                setSelectedSourceMimeTypeKey(`${option?.key}`);
              }}
              options={sourceMimeTypeOptions}
              disabled={!isNew}
              styles={{ root: { maxWidth: 360 } }}
            />
            <div style={{ height: 8 }} />
            <TextField
              label="Source location"
              required
              readOnly={!isNew || !permissions?.isAbleToCreateBatchScoringJobs}
              placeholder={sourcePlaceholder}
              onChange={(_ev, value) => {
                setSourceLocation(value || '');
              }}
              value={sourceLocation}
              styles={{
                root: { maxWidth: 360 },
                field: {
                  color:
                    !isNew || !permissions?.isAbleToCreateBatchScoringJobs
                      ? theme.semanticColors?.textDisabled
                      : undefined,
                },
              }}
            />
            <div style={{ height: 8 }} />
            <Dropdown
              label="Input features in output"
              selectedKey={inputFeaturesSelectedKey}
              onChange={(_ev, option) => {
                setInputFeaturesSelectedKey(option?.key as string);
              }}
              options={[
                {
                  key: 'none',
                  text: 'None',
                },
                {
                  key: 'all',
                  text: 'All',
                },
                {
                  key: 'custom',
                  text: 'Custom',
                },
              ]}
              disabled={!isNew || !permissions.isAbleToCreateBatchScoringJobs}
              styles={{ root: { maxWidth: 360 } }}
            />
            <div style={{ maxWidth: 360 }}>
              {inputFeaturesSelectedKey === 'none' ? (
                <p>The output will contain only the predicted columns.</p>
              ) : inputFeaturesSelectedKey === 'all' ? (
                <p>
                  The output will contain both the prediction results and the corresponding input feature values,
                  allowing easier correlation and debugging
                </p>
              ) : (
                <p>The output will include predicted columns and the specified ID column.</p>
              )}
            </div>
            {inputFeaturesSelectedKey === 'custom' ? (
              <TextField
                description="Column containing the unique identifier for each record. When specified, this field's value will be included in the response to help correlate predictions with input data."
                label="ID Field"
                onChange={(_ev, value) => {
                  setIdField(value || '');
                }}
                readOnly={!isNew || !permissions?.isAbleToCreateBatchScoringJobs}
                styles={{
                  root: { maxWidth: 360 },
                  field: {
                    color:
                      !isNew || !permissions?.isAbleToCreateBatchScoringJobs
                        ? theme.semanticColors?.textDisabled
                        : undefined,
                  },
                }}
                value={idField}
              />
            ) : null}
          </FormSection>
        </div>
        <div>
          <FormSection title="Sink">
            <Dropdown
              label="Sink spec"
              selectedKey={selectedSinkSpecKey}
              onChange={(_ev, option) => {
                setSelectedSinkSpecKey(`${option?.key}`);
                setSinkMimeTypeOptions(
                  option?.data?.supportedMimeTypes?.map(
                    (mimeType: string) =>
                      ({
                        key: mimeType,
                        text: mimeType,
                      } as DropdownOption)
                  )
                );
              }}
              options={sinkOptions}
              disabled={!isNew || !permissions?.isAbleToCreateBatchScoringJobs}
              styles={{ root: { maxWidth: 360 } }}
            />
            {selectedSinkSpecKey && currentSinkSchema ? (
              <Form
                ref={sinkFormRef}
                id={'executor-pool-config-form'}
                idPrefix="sink_spec_"
                validator={validator}
                schema={currentSinkSchema}
                formData={sinkConfig}
                onChange={(schema) => {
                  setSinkConfig(schema.formData);
                }}
                liveValidate={isNew}
                showErrorList={showSinkConfigValidation ? 'bottom' : false}
                readonly={!permissions?.isAbleToCreateBatchScoringJobs || !isNew}
                className={classNames.formEditor}
                uiSchema={getCustomUiSchema(currentSinkSchema)}
                widgets={customWidgets}
              >
                <></>
              </Form>
            ) : null}
            <div style={{ height: 8 }} />
            <Dropdown
              label="Sink MIME type"
              required
              selectedKey={selectedSinkMimeTypeKey}
              onChange={(_ev, option) => {
                setSelectedSinkMimeTypeKey(`${option?.key}`);
              }}
              options={sinkMimeTypeOptions}
              disabled={!isNew}
              styles={{ root: { maxWidth: 360 } }}
            />
            <div style={{ height: 8 }} />
            <TextField
              label="Sink location"
              required
              placeholder={sinkPlaceholder}
              readOnly={!isNew || !permissions?.isAbleToCreateBatchScoringJobs}
              onChange={(_ev, value) => {
                setSinkLocation(value || '');
              }}
              value={sinkLocation}
              styles={{
                root: { maxWidth: 360 },
                field: {
                  color:
                    !isNew || !permissions?.isAbleToCreateBatchScoringJobs
                      ? theme.semanticColors?.textDisabled
                      : undefined,
                },
              }}
            />
          </FormSection>
        </div>
        <div style={{ height: 10 }}></div>

        {selectedModels?.[0] && !isMlopsApiV1_0_x ? (
          <FormSection title="Monitoring">
            <Toggle
              label="Enable monitoring"
              onText="Yes"
              offText="No"
              checked={isMonitoringEnabled}
              disabled={!isNew || !permissions.isAbleToSetBatchScoringMonitoring || isMonitoringGloballyDisabled}
              onChange={(_ev, checked) => {
                setIsMonitoringEnabled(checked || false);
              }}
            />
            {isMonitoringGloballyDisabled && (
              <Info style={{ marginTop: 10 }} styles={{ text: { alignContent: 'center' } }}>
                Monitoring is not allowed in this environment. In case you need it, please contact our support team.
              </Info>
            )}
            <span
              style={
                isMonitoringEnabled
                  ? undefined
                  : { visibility: 'hidden', display: 'block', height: 0, overflow: 'hidden' }
              }
            >
              <Expander
                label={'Monitoring options'}
                onChangeExpanded={(isExpanded) => setAdvancedMonitoringExpanded(isExpanded)}
              />
              <span
                style={
                  isAdvancedMonitoringExpanded
                    ? undefined
                    : { visibility: 'hidden', height: 0, overflow: 'hidden', display: 'block' }
                }
              >
                {experimentId ? (
                  <Monitoring
                    monitoringOptions={monitoringOptions}
                    onChangeMonitoringOptions={onChangeMonitoringOptions}
                    disabled={!isNew || !permissions.isAbleToSetBatchScoringMonitoring}
                    experimentId={experimentId || ''}
                    isExistingMonitoring={isExistingMonitoring}
                    shouldFetchMetadata={isNew || isExistingMonitoring}
                  />
                ) : (
                  <Loader label="Obtaining scoring configurations for selected model..." />
                )}
              </span>
              {isMonitoringEnabled && noMonitoringColumnsSpecified ? (
                <Info
                  style={{ marginTop: 10 }}
                  styles={{
                    text: { alignContent: 'center', color: theme.semanticColors?.inputErrorMessageText },
                    icon: {
                      color: `${theme.semanticColors?.inputErrorMessageText} !important`,
                    },
                  }}
                >
                  At least one column must be specified for monitoring.
                </Info>
              ) : null}
            </span>
          </FormSection>
        ) : null}
      </div>
      <div style={{ height: 25 }}></div>
      {permissions?.isAbleToCreateBatchScoringJobs && isNew ? (
        <Button
          onClick={onActionClick}
          disabled={
            isWidgetLoading ||
            !selectedModels.length ||
            !jobName ||
            !selectedSourceMimeTypeKey ||
            !sourceLocation ||
            !selectedSinkMimeTypeKey ||
            !sinkLocation ||
            isSubmitDisabled ||
            (isMonitoringEnabled && noMonitoringColumnsSpecified)
          }
          styles={buttonStylesPrimary}
        >
          {'Start job'}
        </Button>
      ) : null}
    </div>
  );
};
